import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Register the service worker once on app load. */
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js");
    return reg;
  } catch (e) {
    console.warn("SW registration failed:", e);
    return null;
  }
}

/** Hook for managing push notification subscription. */
export default function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const s = "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
    setSupported(s);
    if (s) {
      navigator.serviceWorker.getRegistration().then(async (reg) => {
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      });
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return toast.error("Tu navegador no soporta notificaciones push");
    setLoading(true);
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permiso denegado. Actívalo desde los ajustes del navegador.");
        return;
      }
      // Get VAPID public key from backend
      const { data: vk } = await api.get("/push/vapid-key");
      if (!vk.public_key) {
        toast.error("Push notifications no están configuradas en el servidor");
        return;
      }
      const reg = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
      if (!reg) throw new Error("No service worker");
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vk.public_key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setSubscribed(true);
      toast.success("Notificaciones activadas");
    } catch (e) {
      console.error("Push subscribe error:", e);
      toast.error("No se pudieron activar las notificaciones");
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const json = sub.toJSON();
          try {
            await api.post("/push/unsubscribe", { endpoint: json.endpoint, keys: json.keys });
          } catch { /* ignore */ }
          await sub.unsubscribe();
        }
      }
      setSubscribed(false);
      toast.success("Notificaciones desactivadas");
    } finally {
      setLoading(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent === 0) toast.warning("No hay dispositivos activos. Activa notificaciones primero.");
      else toast.success(`Enviado a ${data.sent} dispositivo(s)`);
    } catch {
      toast.error("Error enviando prueba");
    } finally { setLoading(false); }
  }, []);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest };
}
